import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import api from '../../services/api';
import { formatDateTime } from '../../utils/formatDate';

const LeadCommentsPage = () => {
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    lead_id: '',
    search: ''
  });

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const response = await api.get('/leads', { params: { limit: 1000 } });
        const list = response.data.data?.leads || [];
        setLeads(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Failed to load leads:', error);
        setLeads([]);
      }
    };

    fetchLeads();
  }, []);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        setLoading(true);
        const params = {
          page,
          limit: pageSize,
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value))
        };
        const response = await api.get('/leads/comments', { params });
        const data = response.data.data || {};
        setComments(Array.isArray(data.comments) ? data.comments : []);
        setPagination(data.pagination || null);
      } catch (error) {
        console.error('Failed to load comments:', error);
        setComments([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [page, pageSize, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dashboard-header">
          <h1>Lead Comments</h1>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-sm-6">
                <label className="form-label small mb-1">Lead</label>
                <select
                  name="lead_id"
                  className="form-select"
                  value={filters.lead_id}
                  onChange={handleFilterChange}
                >
                  <option value="">All leads</option>
                  {leads.map((lead) => (
                    <option key={lead._id} value={lead._id}>
                      {lead.name}{lead.company ? ` (${lead.company})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6">
                <label className="form-label small mb-1">Search</label>
                <input
                  type="text"
                  name="search"
                  className="form-control"
                  placeholder="Search comment text"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card table-responsive">
          {loading ? (
            <Loading />
          ) : (
            <>
              <table className="table table-constrained mb-0 table-hover">
                <colgroup>
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '10%' }} />
                </colgroup>
                <thead className="table-light border-light">
                  <tr>
                    <th>S.No.</th>
                    <th>Comment</th>
                    <th>Lead</th>
                    <th>Company</th>
                    <th>By</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {comments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No comments found
                      </td>
                    </tr>
                  ) : (
                    comments.map((comment, index) => (
                      <tr key={comment._id} className="align-middle">
                        <td className="text-nowrap">{(page - 1) * pageSize + index + 1}</td>
                        <td className="cell-wrap">{comment.comment}</td>
                        <td className="fw-medium cell-wrap">{comment.context?.name || 'N/A'}</td>
                        <td className="cell-wrap">{comment.context?.company || 'N/A'}</td>
                        <td className="cell-wrap">{comment.commented_by?.name || 'N/A'}</td>
                        <td className="cell-wrap">{formatDateTime(comment.created_at)}</td>
                        <td>
                          {comment.context?.link && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => navigate(comment.context.link)}
                            >
                              View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="p-3">
                <Pagination
                  pagination={pagination}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default LeadCommentsPage;
